import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { APP_VERSION, APP_BUILD_LABEL } from '../version';
import { useAuth } from '../contexts/AuthContext';
import { useCurrentPdv } from '../contexts/CurrentPdvContext';
import { pdvApi, docenteKeysApi } from '../lib/api';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  Store,
  FileText,
  GraduationCap,
  ClipboardCheck,
  CalendarDays,
  Calendar,
  Bell,
  Users,
  LogOut,
  Menu,
  X,
  User,
  ChevronDown,
  BookOpen,
  Video,
  Flame,
  FileSpreadsheet,
  Key,
  UserPlus,
  UserCog,
  Award,
  Stethoscope,
  FileSignature,
  ClipboardList,
  Download,
  MapPin,
  Settings,
  FileCog,
  Building2,
  Clock,
  FileCheck,
  UsersRound,
  Link2,
  CalendarClock,
  Grid3x3,
  ShieldCheck,
  HardHat,
  UploadCloud,
  Briefcase,
  Wallet,
  BellRing,
  Thermometer,
  TestTube,
  Presentation,
  Sparkles,
  Mail,
  Rocket,
  AlertTriangle,
  ChevronRight,
  Bot,
  Inbox
} from 'lucide-react';
import { FlaskConical } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import AiAssistant from './AiAssistant';
import SidebarContextSelector from './SidebarContextSelector';

const CENLogo = () => (
  <img 
    src="/cen_logo.png" 
    alt="CEN Engineering" 
    className="w-12 h-12 object-contain"
  />
);

// ============ PERMISSION SYSTEM BASED ON PDF REQUIREMENTS ============
// Navigation items organized in groups with separators as per PDF
const navGroups = {
  main: [
    { path: '/', label: 'Dashboard', icon: LayoutDashboard, key: 'dashboard' },
    { path: '/azienda', label: 'Azienda', icon: Building2, key: 'azienda' },
    { path: '/pdv', label: 'Unità Locale', icon: Store, key: 'pdv' },
    { path: '/personnel', label: 'Personale UL', icon: UserCog, key: 'personnel' },
    { path: '/documents', label: 'Documenti UL', icon: FileText, key: 'documents' },
    { path: '/ground-inspections', label: 'Verifiche di Terra', icon: MapPin, key: 'groundInspections' },
    { path: '/safety-checks', label: 'Controlli UL', icon: ClipboardCheck, key: 'safetyChecks' },
    { path: '/calendar', label: 'Calendario Controlli', icon: CalendarDays, key: 'calendar' },
    { path: '/scadenze', label: 'Scadenze', icon: Clock, key: 'scadenze' },
    { path: '/notifications', label: 'Notifiche', icon: Bell, key: 'notifications' }],
  documents: [
    { path: '/documents-matrix', label: 'Matrice Documenti UL', icon: Grid3x3, key: 'documentsMatrix' },
    { path: '/dvr-duvri', label: 'DVR/DUVRI', icon: FileCheck, key: 'dvrDuvri' },
    { path: '/sicurezza/dichiarazione-luoghi-lavoro', label: 'Dichiarazione Sicurezza Luoghi di Lavoro', icon: FileSignature, key: 'sicurezzaDichiarazione' }],
  haccp_group: [
    { path: '/haccp', label: 'Manuale HACCP', icon: FileText, key: 'haccp' },
    { path: '/haccp/manuali', label: 'Crea Manuale (AI)', icon: Sparkles, key: 'haccpCreaManuale' },
    { path: '/haccp/dichiarazione', label: 'Dichiarazione Autocontrollo HACCP', icon: FileSignature, key: 'haccpDichiarazione' },
    { path: '/banchi-frigo', label: 'Banchi Frigo & Schede T°', icon: Thermometer, key: 'banchiFrigo' },
    { path: '/haccp-temperature', label: 'Schede Temperature', icon: Thermometer, key: 'haccpTemperature' },
    // v107: rimosso 'Verbale Prelievo Campioni' (pagina legacy /haccp-campioni)
    // sostituita da 'Verbali di Prelievo' qui sotto (con workflow Lab completo).
    // La route /haccp-campioni resta valida in App.js per backward compat.
    // { path: '/haccp-campioni', label: 'Verbale Prelievo Campioni', icon: TestTube, key: 'haccpCampioni' },
    { path: '/haccp-cleaning', label: 'Pulizia e Sanificazione', icon: Sparkles, key: 'haccpCleaning' },
    { path: '/haccp-allergens', label: 'Allergeni', icon: AlertTriangle, key: 'haccpAllergens' },
    { path: '/haccp-inspection', label: 'Check-list Ispezione', icon: ClipboardCheck, key: 'haccpInspection' },
    // v1.3.15 — UNIFICATA: "Schede Tecniche Prodotti" (vecchia) e "Schede Prodotto da Rapporti" → un'unica voce qui sotto.
    // La vecchia voce '/haccp/schede-tecniche' resta accessibile via URL diretto per backward-compat ma non è più nel menù.
    { path: '/haccp/libro-ingredienti', label: 'Libro Unico Ingredienti', icon: BookOpen, key: 'haccpLibroIngredienti' },
    { path: '/haccp/schede-prodotto-da-rapporti', label: 'Schede Tecniche Prodotto', icon: Sparkles, key: 'haccpSchedeProdotto' },
    { path: '/haccp/verbali-prelievo', label: 'Verbali di Prelievo', icon: TestTube, key: 'haccpVerbaliPrelievo' },
    { path: '/haccp/schede-preoperative', label: 'Schede Controllo Pre-Operativo', icon: ClipboardCheck, key: 'haccpSchedePreoperative' },
    { path: '/haccp/matrice-prelievi', label: 'Storico Campionamenti', icon: ClipboardCheck, key: 'haccpMatricePrelievi' },
    { path: '/haccp/verbali-asl', label: 'Verbali ASL & Prescrizioni', icon: AlertTriangle, key: 'haccpVerbaliAsl' },
    // v103: rimossa voce duplicata 'Laboratorio Analisi' (era una vecchia pagina,
    // la sostituisce 'Verbali di Prelievo' qui sopra). La route /lab-analysis
    // resta valida in App.js per backward compat ma non appare in sidebar.
    // { path: '/lab-analysis', label: 'Laboratorio Analisi', icon: TestTube, key: 'labAnalysis' },
    // v38: voce upsell per azienda/UL senza licenza HACCP attiva.
    // Punta alla stessa pagina /haccp-licenses ma è etichettata come "Attiva HACCP"
    // ed è l'UNICA voce visibile dentro la macro-area HACCP per questi utenti.
    { path: '/haccp-licenses', label: 'Attiva HACCP', icon: Key, key: 'haccpActivate' }],
  personnel: [
    { path: '/employees-overview', label: 'Riepilogo Dipendenti', icon: Users, key: 'employeesOverview' },
    { path: '/certificates', label: 'Attestati Personale UL', icon: Award, key: 'certificates' },
    { path: '/training-certificates', label: 'Scadenze Formazione', icon: CalendarClock, key: 'trainingCertificates' },
    { path: '/medical-records', label: 'Scadenze Visite Mediche', icon: Stethoscope, key: 'medicalRecordsScadenze' },
    { path: '/document-generator', label: 'Genera Documenti', icon: FileSignature, key: 'documentGenerator' },
    { path: '/docx-templates-manager', label: 'Gestione Template', icon: FileText, key: 'docxTemplatesManager' },
    { path: '/dpi-catalog', label: 'Catalogo DPI x Mansione', icon: HardHat, key: 'dpiCatalog' },
    { path: '/medical-visits', label: 'Visite Mediche (legacy)', icon: Stethoscope, key: 'medicalVisits' }],
  docenti: [
    { path: '/teachers', label: 'Docenti', icon: BookOpen, key: 'teachers' },
    { path: '/enrolled-personnel', label: 'Personale Iscritto', icon: UsersRound, key: 'enrolledPersonnel' },
    { path: '/training', label: 'Formazione', icon: GraduationCap, key: 'training' },
    { path: '/virtual-classrooms', label: 'Aule Virtuali', icon: Video, key: 'virtualClassrooms' },
    { path: '/attendance-registers', label: 'Registri Formazione UL', icon: ClipboardList, key: 'attendanceRegisters' },
    { path: '/training-sessions', label: 'Registri Presenze Pubblici', icon: Link2, key: 'trainingSessions' },
    { path: '/formazione/gestione-template', label: 'Gestione Template', icon: FileCog, key: 'gestioneTemplateFormazione' },
    { path: '/safety-programs', label: 'Programmi Sicurezza', icon: GraduationCap, key: 'safetyPrograms' },
    { path: '/training-materials', label: 'Materiale Didattico', icon: BookOpen, key: 'trainingMaterials' },
    { path: '/training-tests', label: 'Test di Formazione', icon: FileCheck, key: 'trainingTests' },
    { path: '/test-submissions', label: 'Test Ricevuti', icon: Inbox, key: 'testSubmissions' },
    { path: '/docente/ppt', label: 'PowerPoint Docenti', icon: Presentation, key: 'docentePpt' },
    { path: '/docente-keys', label: 'Le mie Chiavi', icon: Key, key: 'docenteKeys' },
    { path: '/teacher-invoices', label: 'Fatture Docenti', icon: FileSpreadsheet, key: 'teacherInvoices' }],
  // v38d: Modulo Studio Tecnico (ERP per studi architettura/ingegneria)
  studio: [
    { path: '/studio', label: 'Dashboard Studio', icon: LayoutDashboard, key: 'studioDashboard' },
    { path: '/studio/calendario', label: 'Calendario Impegni', icon: Calendar, key: 'studioCalendario' },
    { path: '/studio/commesse', label: 'Commesse', icon: Briefcase, key: 'studioCommesse' },
    { path: '/studio/clienti', label: 'Clienti Studio', icon: Users, key: 'studioClienti' },
    { path: '/studio/preventivi', label: 'Preventivi', icon: FileSpreadsheet, key: 'studioPreventivi' },
    { path: '/studio/pagamenti', label: 'Pagamenti', icon: Wallet, key: 'studioPagamenti' },
    { path: '/studio/scadenze', label: 'Scadenze Studio', icon: BellRing, key: 'studioScadenze' },
    { path: '/studio/insegne', label: 'Insegne & Affissioni', icon: FileSignature, key: 'studioInsegne' },
    { path: '/studio/fatture', label: 'Fatture Elettroniche', icon: FileSpreadsheet, key: 'studioFatture' },
    { path: '/studio/modulistica', label: 'Modulistica Pratiche', icon: FileText, key: 'studioModulistica' },
    { path: '/sicurezza/piani-sanitari', label: 'Piani Sanitari Eventi (C.O. 118)', icon: FileText, key: 'pianiSanitari' },
    { path: '/studio/contratti', label: 'Contratti & Firme', icon: FileSignature, key: 'studioContratti' },
    { path: '/studio/deleghe', label: 'Deleghe (DPR 445/2000)', icon: FileSignature, key: 'studioDeleghe' },
    { path: '/studio/delega-docfa', label: 'Delega DOCFA & Planimetria Catastale', icon: FileText, key: 'studioDelegaDocfa' },
    { path: '/studio/impianti-audiovisivi', label: 'Impianti Audiovisivi', icon: Video, key: 'studioImpiantiAV' },
    { path: '/studio/prevenzione-incendi', label: 'Prevenzione Incendi', icon: Flame, key: 'studioPrevenzioneIncendi' },
    { path: '/studio/agente-collab', label: 'Collaboratore AI Iscrizioni', icon: Bot, key: 'studioAgenteCollab' },
    { path: '/studio/piattaforme-formazione', label: 'Agente Iscrizioni', icon: Bot, key: 'studioPiattaforme' },
    { path: '/signflow', label: 'SignFlow', icon: FileSignature, key: 'signflow' }],
  admin: [
    { path: '/users', label: 'Utenti', icon: Users, key: 'users' },
    { path: '/registrations', label: 'Richieste Accesso', icon: UserPlus, key: 'registrations' },
    { path: '/licenses', label: 'Licenze', icon: Key, key: 'licenses' },
    { path: '/haccp-licenses', label: 'Licenze HACCP', icon: Thermometer, key: 'haccpLicenses' },
    { path: '/professionista', label: 'Profilo Professionista', icon: Stethoscope, key: 'professionista' },
    { path: '/notifiche', label: 'Notifiche & Promemoria', icon: Bell, key: 'notificheCron' },
    { path: '/notifications-settings', label: 'Impostazioni Notifiche', icon: Settings, key: 'notificationsSettings' },
    { path: '/admin/template-editor', label: 'Template Editor', icon: FileCog, key: 'adminTemplateEditor' },
    { path: '/admin/online-users', label: 'Utenti Online', icon: UsersRound, key: 'onlineUsers' }],
};

// Role-based permissions - which keys each role can see
const rolePermissions = {
  super_admin: {
    main: ['dashboard', 'azienda', 'pdv', 'personnel', 'documents', 'groundInspections', 'safetyChecks', 'calendar', 'scadenze', 'notifications'],
    documents: ['documentsMatrix', 'dvrDuvri', 'sicurezzaDichiarazione'],
    haccp_group: ['haccp', 'banchiFrigo', 'haccpTemperature', 'haccpCampioni', 'haccpCleaning', 'haccpAllergens', 'haccpInspection', 'haccpSchedeTecniche', 'haccpLibroIngredienti', 'haccpSchedeProdotto', 'haccpDichiarazione', 'haccpVerbaliPrelievo', 'haccpSchedePreoperative', 'haccpMatricePrelievi', 'haccpVerbaliAsl', 'haccpCreaManuale'],
    personnel: ['employeesOverview', 'certificates', 'trainingCertificates', 'medicalRecordsScadenze', 'documentGenerator', 'dpiCatalog', 'docxTemplatesManager', 'medicalVisits'],
    docenti: ['teachers', 'enrolledPersonnel', 'training', 'virtualClassrooms', 'attendanceRegisters', 'trainingSessions', 'gestioneTemplateFormazione', 'docentePpt', 'registerTemplates', 'safetyPrograms', 'trainingMaterials', 'trainingTests', 'testSubmissions', 'teacherInvoices'],
    admin: ['users', 'registrations', 'licenses', 'haccpLicenses', 'professionista', 'notificationsSettings', 'notificheCron', 'adminTemplateEditor', 'onlineUsers'],
    studio: ['studioDashboard', 'studioCalendario', 'studioCommesse', 'studioClienti', 'studioPreventivi', 'studioPagamenti', 'studioScadenze', 'studioInsegne', 'studioFatture', 'studioModulistica', 'pianiSanitari', 'studioContratti', 'studioDeleghe', 'studioDelegaDocfa', 'studioImpiantiAV', 'studioPrevenzioneIncendi', 'studioAgenteCollab', 'studioPiattaforme', 'signflow'],
  },
  admin: {
    main: ['dashboard', 'azienda', 'pdv', 'personnel', 'documents', 'groundInspections', 'safetyChecks', 'calendar', 'scadenze', 'notifications'],
    documents: ['documentsMatrix', 'dvrDuvri', 'sicurezzaDichiarazione'],
    haccp_group: ['haccp', 'banchiFrigo', 'haccpTemperature', 'haccpCampioni', 'haccpCleaning', 'haccpAllergens', 'haccpInspection', 'haccpSchedeTecniche', 'haccpLibroIngredienti', 'haccpSchedeProdotto', 'haccpDichiarazione', 'haccpVerbaliPrelievo', 'haccpSchedePreoperative', 'haccpMatricePrelievi', 'haccpVerbaliAsl', 'haccpCreaManuale'],
    personnel: ['employeesOverview', 'certificates', 'trainingCertificates', 'medicalRecordsScadenze', 'documentGenerator', 'dpiCatalog', 'docxTemplatesManager', 'medicalVisits'],
    docenti: ['teachers', 'enrolledPersonnel', 'training', 'virtualClassrooms', 'attendanceRegisters', 'trainingSessions', 'gestioneTemplateFormazione', 'docentePpt', 'registerTemplates', 'safetyPrograms', 'trainingMaterials', 'trainingTests', 'testSubmissions', 'teacherInvoices'],
    admin: ['users', 'licenses', 'haccpLicenses', 'professionista', 'notificationsSettings', 'notificheCron', 'adminTemplateEditor', 'onlineUsers'],
    studio: ['studioDashboard', 'studioCalendario', 'studioCommesse', 'studioClienti', 'studioPreventivi', 'studioPagamenti', 'studioScadenze', 'studioInsegne', 'studioFatture', 'studioModulistica', 'pianiSanitari', 'studioContratti', 'studioDeleghe', 'studioDelegaDocfa', 'studioImpiantiAV', 'studioPrevenzioneIncendi', 'studioAgenteCollab', 'studioPiattaforme', 'signflow'],
  },
  amministratore: {
    main: ['dashboard', 'azienda', 'pdv', 'personnel', 'documents', 'groundInspections', 'safetyChecks', 'calendar', 'scadenze', 'notifications'],
    documents: ['documentsMatrix', 'dvrDuvri', 'sicurezzaDichiarazione'],
    haccp_group: ['haccp', 'banchiFrigo', 'haccpTemperature', 'haccpCampioni', 'haccpCleaning', 'haccpAllergens', 'haccpInspection', 'haccpSchedeTecniche', 'haccpLibroIngredienti', 'haccpSchedeProdotto', 'haccpDichiarazione', 'haccpVerbaliPrelievo', 'haccpSchedePreoperative', 'haccpMatricePrelievi', 'haccpVerbaliAsl', 'haccpCreaManuale'],
    personnel: ['employeesOverview', 'certificates', 'trainingCertificates', 'medicalRecordsScadenze', 'documentGenerator', 'dpiCatalog', 'docxTemplatesManager', 'medicalVisits'],
    docenti: ['teachers', 'enrolledPersonnel', 'training', 'virtualClassrooms', 'attendanceRegisters', 'trainingSessions', 'gestioneTemplateFormazione', 'docentePpt', 'registerTemplates', 'safetyPrograms', 'trainingMaterials', 'trainingTests', 'testSubmissions', 'teacherInvoices'],
    admin: ['users', 'licenses', 'haccpLicenses', 'professionista', 'notificationsSettings', 'notificheCron', 'adminTemplateEditor', 'onlineUsers'],
    studio: ['studioDashboard', 'studioCalendario', 'studioCommesse', 'studioClienti', 'studioPreventivi', 'studioPagamenti', 'studioScadenze', 'studioInsegne', 'studioFatture', 'studioModulistica', 'pianiSanitari', 'studioContratti', 'studioDeleghe', 'studioDelegaDocfa', 'studioImpiantiAV', 'studioPrevenzioneIncendi', 'studioAgenteCollab', 'studioPiattaforme', 'signflow'],
  },
  rspp: {
    main: ['dashboard', 'azienda', 'pdv', 'personnel', 'documents', 'groundInspections', 'safetyChecks', 'calendar', 'scadenze', 'notifications'],
    documents: ['documentsMatrix', 'dvrDuvri', 'sicurezzaDichiarazione'],
    haccp_group: ['haccp', 'banchiFrigo', 'haccpTemperature', 'haccpCampioni', 'haccpCleaning', 'haccpAllergens', 'haccpInspection', 'haccpSchedeTecniche', 'haccpLibroIngredienti', 'haccpSchedeProdotto', 'haccpDichiarazione', 'haccpVerbaliPrelievo', 'haccpSchedePreoperative', 'haccpMatricePrelievi', 'haccpVerbaliAsl', 'haccpCreaManuale'],
    personnel: ['employeesOverview', 'certificates', 'trainingCertificates', 'medicalRecordsScadenze', 'documentGenerator', 'dpiCatalog', 'docxTemplatesManager', 'medicalVisits'],
    docenti: ['teachers', 'enrolledPersonnel', 'training', 'virtualClassrooms', 'attendanceRegisters', 'trainingSessions', 'gestioneTemplateFormazione', 'registerTemplates', 'safetyPrograms', 'trainingMaterials', 'trainingTests', 'testSubmissions', 'teacherInvoices', 'docentePpt'],
    admin: ['haccpLicenses', 'notificationsSettings', 'adminTemplateEditor'],
    studio: ['pianiSanitari'],
  },
  consulente_haccp: {
    // Consulente HACCP: vede SOLO le aziende/UL che ha creato lui o che gli
    // sono state assegnate dall'amministratore. Pieno accesso a tutti i moduli HACCP.
    main: ['dashboard', 'azienda', 'pdv', 'personnel', 'documentsMatrix', 'documents', 'safetyChecks', 'scadenze', 'notifications'],
    documents: ['documentsMatrix', 'dvrDuvri', 'sicurezzaDichiarazione'],
    haccp_group: ['haccp', 'banchiFrigo', 'haccpTemperature', 'haccpCampioni', 'haccpCleaning', 'haccpAllergens', 'haccpInspection', 'labAnalysis', 'haccpSchedeTecniche', 'haccpLibroIngredienti', 'haccpSchedeProdotto', 'haccpDichiarazione', 'haccpVerbaliPrelievo', 'haccpSchedePreoperative', 'haccpMatricePrelievi', 'haccpVerbaliAsl', 'haccpCreaManuale'],
    personnel: ['certificates', 'medicalRecordsScadenze'],
    docenti: [],
    admin: [],
    studio: ['pianiSanitari'],
  },
  laboratorio_analisi: {
    // Solo verbali assegnati + propri referti
    main: ['dashboard', 'notifications'],
    documents: [],
    haccp_group: ['labAnalysis'],
    personnel: [],
    docenti: [],
    admin: [],
  },
  collaborator: {
    main: ['dashboard', 'azienda', 'pdv', 'personnel', 'documentsMatrix', 'documents', 'documentGenerator', 'dvrDuvri', 'groundInspections', 'safetyChecks', 'scadenze', 'notifications'],
    documents: ['documentsMatrix', 'dvrDuvri', 'sicurezzaDichiarazione'],
    haccp_group: ['haccp', 'banchiFrigo', 'haccpTemperature', 'haccpCampioni', 'haccpCleaning', 'haccpAllergens', 'haccpInspection', 'haccpActivate', 'haccpSchedeTecniche', 'haccpLibroIngredienti', 'haccpSchedeProdotto', 'haccpDichiarazione', 'haccpVerbaliPrelievo', 'haccpSchedePreoperative', 'haccpMatricePrelievi', 'haccpVerbaliAsl', 'haccpCreaManuale'],
    personnel: ['certificates', 'trainingCertificates', 'medicalRecordsScadenze', 'medicalVisits'],
    docenti: [],
    admin: [],
  },
  // v38e: Tecnico-Collaboratore — ruolo flessibile per consulenti tecnici esterni.
  // Vede TUTTI i moduli, ma l'admin può restringere tramite "visible_sections"
  // (campo già esistente sull'utente). Se visible_sections è VUOTO → vede tutto;
  // se contiene chiavi → vede solo quelle. Questo permette di personalizzare
  // l'accesso per ogni collaboratore.
  tecnico_collaboratore: {
    main: ['dashboard', 'azienda', 'pdv', 'personnel', 'documentsMatrix', 'documents', 'documentGenerator', 'dvrDuvri', 'groundInspections', 'safetyChecks', 'calendar', 'scadenze', 'notifications'],
    documents: ['documentsMatrix', 'dvrDuvri', 'sicurezzaDichiarazione'],
    haccp_group: ['haccp', 'banchiFrigo', 'haccpTemperature', 'haccpCampioni', 'haccpCleaning', 'haccpAllergens', 'haccpInspection', 'haccpActivate', 'haccpSchedeTecniche', 'haccpLibroIngredienti', 'haccpSchedeProdotto', 'haccpDichiarazione', 'haccpVerbaliPrelievo', 'haccpSchedePreoperative', 'haccpMatricePrelievi', 'haccpVerbaliAsl', 'haccpCreaManuale'],
    personnel: ['employeesOverview', 'certificates', 'trainingCertificates', 'medicalRecordsScadenze', 'documentGenerator', 'dpiCatalog', 'docxTemplatesManager', 'medicalVisits'],
    docenti: ['teachers', 'enrolledPersonnel', 'training', 'virtualClassrooms', 'attendanceRegisters', 'trainingSessions', 'gestioneTemplateFormazione', 'registerTemplates', 'safetyPrograms', 'trainingMaterials', 'trainingTests', 'testSubmissions', 'teacherInvoices'],
    admin: ['notifications'],
    studio: ['studioDashboard', 'studioCalendario', 'studioCommesse', 'studioClienti', 'studioPreventivi', 'studioPagamenti', 'studioScadenze', 'studioInsegne', 'studioFatture', 'studioModulistica', 'pianiSanitari', 'studioContratti'],
  },
  azienda: {
    // Azienda: vede l'intera Gestione Aziendale come l'amministratore
    // (Dashboard, Azienda, UL, Personale, Matrice, Documenti) + Sicurezza.
    // HACCP: voce "Check-list Ispezione" sempre visibile; le voci HACCP complete
    // appaiono solo se l'azienda possiede almeno una UL con requires_haccp=true
    // (gating tramite user.has_haccp_units dal backend).
    main: ['dashboard', 'azienda', 'pdv', 'personnel', 'documentsMatrix', 'documents', 'documentGenerator', 'dvrDuvri', 'groundInspections', 'safetyChecks', 'calendar', 'scadenze', 'notifications'],
    documents: ['documentsMatrix', 'dvrDuvri', 'sicurezzaDichiarazione'],
    haccp_group: ['haccp', 'banchiFrigo', 'haccpTemperature', 'haccpCampioni', 'haccpCleaning', 'haccpAllergens', 'haccpInspection', 'haccpActivate', 'haccpSchedeTecniche', 'haccpLibroIngredienti', 'haccpSchedeProdotto', 'haccpDichiarazione', 'haccpVerbaliPrelievo', 'haccpSchedePreoperative', 'haccpMatricePrelievi', 'haccpVerbaliAsl', 'haccpCreaManuale'],
    personnel: ['certificates', 'trainingCertificates', 'medicalRecordsScadenze', 'medicalVisits'],
    docenti: [],
    admin: [],
  },
  gerente: {
    main: ['dashboard', 'azienda', 'pdv', 'personnel', 'documentsMatrix', 'documents', 'groundInspections', 'safetyChecks', 'scadenze', 'notifications'],
    documents: ['documentsMatrix', 'dvrDuvri', 'sicurezzaDichiarazione'],
    haccp_group: ['haccp', 'banchiFrigo', 'haccpTemperature', 'haccpCampioni', 'haccpCleaning', 'haccpAllergens', 'haccpInspection', 'haccpActivate', 'haccpSchedeTecniche', 'haccpLibroIngredienti', 'haccpSchedeProdotto', 'haccpDichiarazione', 'haccpVerbaliPrelievo', 'haccpSchedePreoperative', 'haccpMatricePrelievi', 'haccpVerbaliAsl', 'haccpCreaManuale'],
    personnel: ['certificates', 'trainingCertificates', 'medicalRecordsScadenze', 'medicalVisits'],
    docenti: [],
    admin: [],
  },
  medico_competente: {
    // Solo visite mediche + scadenze; NO verifiche di terra, NO calendario
    main: ['dashboard', 'azienda', 'pdv', 'scadenze', 'notifications'],
    documents: [],
    personnel: ['medicalRecordsScadenze', 'medicalVisits'],
    docenti: [],
    admin: [],
  },
  docente: {
    // Docente: nessuna gestione di aziende/UL/docenti. Vede solo le sue
    // attività di insegnamento e i corsi/registri/personale iscritto.
    main: ['dashboard', 'notifications'],
    documents: [],
    personnel: ['documentGenerator'],
    docenti: ['enrolledPersonnel', 'training', 'virtualClassrooms', 'attendanceRegisters', 'trainingSessions', 'gestioneTemplateFormazione', 'registerTemplates', 'safetyPrograms', 'trainingMaterials', 'trainingTests', 'testSubmissions', 'teacherInvoices', 'docentePpt'],
    admin: [],
  },
  docente_online: {
    // v1.1.1: Docente Online — profilo per docenti esterni che fanno corsi in
    // videoconferenza. Vede SOLO i propri dati (filtraggio backend per
    // owner_user_id). Può vedere/inserire i propri:
    // - Docenti (solo se stesso)
    // - Aule Virtuali, Registri Presenze Pubblici
    // - PowerPoint, Template Registri (read-only su quelli di sistema, può creare i suoi)
    // - Programmi Sicurezza, Materiale Didattico, Test di Formazione (solo i suoi)
    // - Test Ricevuti, Fatture Docenti
    // - "Le mie Chiavi" (saldo + acquisto Stripe)
    // Niente Aziende/UL/Personale. Niente "Azioni Rapide" sulla Dashboard.
    main: ['dashboard', 'notifications'],
    documents: [],
    personnel: ['documentGenerator'],
    docenti: ['teachers', 'trainingSessions', 'gestioneTemplateFormazione', 'registerTemplates', 'safetyPrograms', 'trainingTests', 'testSubmissions', 'trainingMaterials', 'teacherInvoices', 'virtualClassrooms', 'docentePpt', 'docenteKeys'],
    admin: [],
  },
  creator: {
    // Only materials
    main: ['dashboard', 'azienda', 'pdv', 'documents', 'groundInspections', 'calendar', 'notifications'],
    documents: [],
    personnel: [],
    docenti: ['trainingMaterials'],
    admin: [],
  },
  verificatore: {
    // Only verification
    main: ['dashboard', 'azienda', 'pdv', 'documents', 'groundInspections', 'calendar', 'notifications'],
    documents: [],
    personnel: [],
    docenti: [],
    admin: [],
  },
  societa: {
    // Supervises companies - NO Controlli Sicurezza, NO Genera Documenti, NO Calendario
    main: ['dashboard', 'azienda', 'pdv', 'documents', 'groundInspections', 'scadenze', 'notifications'],
    documents: [],
    personnel: ['personnel', 'certificates', 'trainingCertificates', 'medicalRecordsScadenze', 'medicalVisits'],
    docenti: [],
    admin: [],
  },
  formatore: {
    // Content Creator: cura i contenuti formativi (template, materiali, test,
    // aule virtuali, registri presenze, sessioni pubbliche).
    // v38b: accesso completo a tutto ciò che è formativo per poter modificare
    // e sistemare per tutti gli utenti.
    main: ['dashboard', 'notifications'],
    documents: [],
    personnel: ['templateVariables', 'docxTemplatesManager'],
    docenti: [
      'teachers',
      'enrolledPersonnel',
      'training',
      'virtualClassrooms',
      'attendanceRegisters',
      'trainingSessions', 'gestioneTemplateFormazione',
      'registerTemplates',
      'safetyPrograms',
      'trainingMaterials',
      'trainingTests',
      'testSubmissions',
      'docentePpt'],
    admin: [],
  },
};

// Map dei valori "visible_sections" (configurati dall'admin in UsersPage)
// alle chiavi (key) dei singoli item del menu in navGroups.
// Se una "section" non è inclusa in visible_sections, gli item che la mappano
// vengono nascosti dalla sidebar.
const SECTION_TO_NAV_KEYS = {
  dashboard: ['dashboard'],
  azienda: ['azienda'],
  unita_locali: ['pdv', 'azienda'],
  personale: ['personnel', 'employeesOverview'],
  matrice_documenti: ['documentsMatrix'],
  documenti: ['documents'],
  dvr_duvri: ['dvrDuvri', 'sicurezzaDichiarazione'],
  haccp_autocontrollo: ['haccp', 'banchiFrigo', 'haccpTemperature', 'haccpCampioni', 'haccpCleaning', 'haccpAllergens', 'haccpInspection', 'labAnalysis', 'haccpActivate', 'haccpSchedeTecniche', 'haccpLibroIngredienti', 'haccpSchedeProdotto', 'haccpDichiarazione', 'haccpVerbaliPrelievo', 'haccpSchedePreoperative', 'haccpMatricePrelievi', 'haccpVerbaliAsl', 'haccpCreaManuale'],
  controlli_sicurezza: ['safetyChecks'],
  visite_mediche: ['medicalRecordsScadenze', 'medicalVisits'],
  // "formazione" è un alias macro che attiva TUTTI gli item formativi (legacy)
  formazione: [
    'training',
    'enrolledPersonnel',
    'virtualClassrooms',
    'attendanceRegisters',
    'trainingSessions', 'gestioneTemplateFormazione',
    'studioPiattaforme',
    'studioAgenteCollab',
    'signflow',
    'registerTemplates',
    'safetyPrograms',
    'trainingTests',
    'testSubmissions',
    'trainingCertificates',
    'teachers',
    'teacherInvoices',
    'trainingMaterials'],
  // Sezioni granulari (più recenti) — preferite per ruoli specifici come docente_online
  aule_virtuali: ['virtualClassrooms', 'attendanceRegisters', 'enrolledPersonnel'],
  registri_presenze_pubblici: ['trainingSessions'],
  template_registri: ['registerTemplates', 'safetyPrograms'],
  test_formazione: ['trainingTests'],
  fatture_docenti: ['teacherInvoices'],
  le_mie_chiavi: [],
  calendario: ['calendar'],
  notifiche: ['notifications'],
  scadenze: ['scadenze', 'trainingCertificates', 'medicalRecordsScadenze'],
  genera_documenti: ['documentGenerator', 'templateVariables', 'dpiCatalog', 'docxTemplatesManager'],
  verifiche_terra: ['groundInspections'],
  certificati: ['certificates'],
  materiale_didattico: ['trainingMaterials'],
  // v38c-d
  programmi_sicurezza: ['safetyPrograms'],
  studio_tecnico: ['studioDashboard', 'studioCalendario', 'studioCommesse', 'studioClienti', 'studioPreventivi', 'studioPagamenti', 'studioScadenze', 'studioInsegne', 'studioFatture', 'studioModulistica', 'pianiSanitari', 'studioContratti', 'studioDeleghe', 'studioDelegaDocfa', 'studioPrevenzioneIncendi', 'studioAgenteCollab', 'studioPiattaforme', 'signflow'],
};

// Costruisce l'insieme di "key" della sidebar che sono ammessi da
// `visible_sections`. Restituisce `null` quando non c'è alcun filtro applicato
// (ovvero l'utente non ha personalizzazioni e va seguito il default del ruolo).
const buildAllowedKeysFromVisibleSections = (visibleSections, role) => {
  if (!Array.isArray(visibleSections) || visibleSections.length === 0) {
    return null;
  }
  // AUTO-INJECT v18: per i ruoli operativi, se la matrice/azienda/documenti
  // non sono nelle visible_sections (utenti creati prima della rinomina),
  // li aggiungiamo automaticamente al volo. Cosi' non serve riaprire ogni
  // utente per spuntarli manualmente.
  const autoInjectByRole = {
    azienda:           ['azienda', 'matrice_documenti', 'documenti', 'haccp_autocontrollo'],
    admin:             ['azienda', 'matrice_documenti', 'documenti'],
    amministratore:    ['azienda', 'matrice_documenti', 'documenti'],
    rspp:              ['azienda', 'matrice_documenti', 'documenti'],
    collaborator:      ['azienda', 'matrice_documenti', 'documenti', 'haccp_autocontrollo'],
    gerente:           ['matrice_documenti', 'documenti', 'haccp_autocontrollo'],
    consulente_haccp:  ['matrice_documenti', 'documenti'],
    tecnico_collaboratore: [],  // v38e: nessun auto-inject — l'admin sceglie tutto manualmente
  };
  const effectiveSections = [...visibleSections];
  const toInject = autoInjectByRole[role] || [];
  toInject.forEach((s) => {
    if (!effectiveSections.includes(s)) effectiveSections.push(s);
  });
  const allowed = new Set();
  effectiveSections.forEach((section) => {
    const keys = SECTION_TO_NAV_KEYS[section] || [];
    keys.forEach((k) => allowed.add(k));
  });
  return allowed;
};

// Helper to filter items based on role permissions
const getFilteredItems = (groupKey, role, allowedModules = [], extraModules = []) => {
  const perms = rolePermissions[role] || rolePermissions.collaborator;
  let allowedKeys = perms[groupKey] || [];
  const items = navGroups[groupKey] || [];

  // v1.2.5: Se l'utente ha questo gruppo nei "moduli extra", abilita TUTTE le voci del gruppo
  // (come se fosse super_admin per quel gruppo). Utile per dare es. Studio Tecnico al consulente HACCP.
  if (Array.isArray(extraModules) && extraModules.includes(groupKey)) {
    allowedKeys = items.map(i => i.key);
  }
  
  // Special handling for formatore - filter docenti by allowed_modules (se presenti)
  // v38: se allowed_modules è vuoto, NON filtrare ulteriormente: rispetta i
  // permessi del ruolo (rolePermissions.formatore.docenti).
  if (role === 'formatore' && groupKey === 'docenti' && Array.isArray(allowedModules) && allowedModules.length > 0) {
    const moduleMap = {
      'materiale_didattico': 'trainingMaterials',
      'formazione': 'training',
      'registri_presenze': 'attendanceRegisters',
      'aule_virtuali': 'virtualClassrooms',
      'template_registri': 'registerTemplates',
      'test_formazione': 'trainingTests',
    };
    const allowedDocentiKeys = allowedModules.map(m => moduleMap[m]).filter(Boolean);
    return items.filter(item => allowedKeys.includes(item.key) && allowedDocentiKeys.includes(item.key));
  }
  
  return items.filter(item => allowedKeys.includes(item.key));
};

// Role display names
const roleDisplayNames = {
  super_admin: 'Super Admin',
  admin: 'Amministratore',
  collaborator: 'Azienda',
  gerente: 'Unità Locale',
  medico_competente: 'Medico Competente',
  docente: 'Docente',
  creator: 'Creator',
  verificatore: 'Verificatore',
  societa: 'Società',
  formatore: 'Content Creator',
  consulente_haccp: 'Consulente HACCP',
  laboratorio_analisi: 'Laboratorio Analisi',
  tecnico_collaboratore: 'Tecnico-Collaboratore',
};

// ===========================================================================
//  MACRO-AREE — sidebar a 5 sezioni collassabili (default: chiuse)
// ===========================================================================
// Mappa: macro-area → array di "keys" navigation che compongono la sezione.
// Le keys vengono filtrate per ruolo e per visible_sections quindi qui basta
// elencarle nell'ordine desiderato.
// Sequenza: Gestione Aziendale → Sicurezza Compliance e Documenti → HACCP → Formazione → Amministrazione
const MACRO_AREAS = [
  {
    id: 'gestione',
    label: 'Gestione Aziendale',
    icon: Briefcase,
    accent: 'from-blue-500/15 to-blue-600/5 border-blue-500/30',
    iconBg: 'bg-blue-500',
    keys: ['dashboard', 'azienda', 'pdv', 'personnel', 'documentsMatrix', 'documents'],
  },
  {
    id: 'sicurezza',
    label: 'Sicurezza Compliance e Documenti',
    icon: ShieldCheck,
    accent: 'from-emerald-500/15 to-emerald-600/5 border-emerald-500/30',
    iconBg: 'bg-emerald-600',
    keys: ['documentGenerator', 'templateVariables', 'docxTemplatesManager', 'dpiCatalog', 'dvrDuvri', 'groundInspections', 'safetyChecks', 'calendar', 'scadenze', 'employeesOverview', 'trainingCertificates', 'medicalRecordsScadenze', 'certificates', 'medicalVisits'],
  },
  {
    id: 'haccp',
    label: 'Autocontrollo HACCP',
    icon: Thermometer,
    accent: 'from-orange-500/15 to-orange-600/5 border-orange-500/30',
    iconBg: 'bg-orange-500',
    keys: ['haccp', 'banchiFrigo', 'haccpTemperature', 'haccpCampioni', 'haccpCleaning', 'haccpAllergens', 'haccpInspection', 'labAnalysis', 'haccpActivate', 'haccpSchedeTecniche', 'haccpLibroIngredienti', 'haccpSchedeProdotto', 'haccpDichiarazione', 'haccpVerbaliPrelievo', 'haccpSchedePreoperative', 'haccpMatricePrelievi', 'haccpVerbaliAsl', 'haccpCreaManuale'],
  },
  {
    id: 'formazione',
    label: 'Formazione',
    icon: GraduationCap,
    accent: 'from-purple-500/15 to-purple-600/5 border-purple-500/30',
    iconBg: 'bg-purple-500',
    keys: ['teachers', 'enrolledPersonnel', 'training', 'virtualClassrooms', 'attendanceRegisters', 'trainingSessions', 'gestioneTemplateFormazione', 'studioPiattaforme', 'studioAgenteCollab', 'signflow', 'docentePpt', 'docenteKeys', 'registerTemplates', 'safetyPrograms', 'trainingMaterials', 'trainingTests', 'testSubmissions', 'teacherInvoices'],
  },
  {
    id: 'studio',
    label: 'Studio Tecnico',
    icon: Briefcase,
    accent: 'from-indigo-500/15 to-indigo-600/5 border-indigo-500/30',
    iconBg: 'bg-indigo-600',
    keys: [
      'studioDashboard', 'studioCalendario',
      'studioCommesse',
      'studioClienti',
      'studioPreventivi',
      'studioPagamenti',
      'studioScadenze',
      'studioInsegne',
      'studioFatture',
      'studioModulistica',
      'pianiSanitari',
      'studioContratti',
      'studioDeleghe', 'studioDelegaDocfa',
      'studioImpiantiAV', 'studioPrevenzioneIncendi',
      'studioAgenteCollab',
      'studioPiattaforme',
      'signflow'],
  },
  // v56: Amministrazione spostata in ultima posizione (su richiesta utente)
  {
    id: 'admin',
    label: 'Amministrazione',
    icon: Settings,
    accent: 'from-slate-500/15 to-slate-600/5 border-slate-500/30',
    iconBg: 'bg-slate-600',
    keys: ['notifications', 'users', 'registrations', 'licenses', 'haccpLicenses', 'professionista', 'notificationsSettings', 'adminTemplateEditor'],
    adminOnly: false,
  }];

// v38: Macro-area dedicata al ruolo "Content Creator" (formatore).
// Sostituisce TUTTE le macro standard per questo ruolo, aggregando tutto
// ciò che il content creator può fare:
// - Template (Registri, DOCX Documenti, Variabili)
// - Materiale Didattico, Test di Formazione
// - Aule Virtuali, Registri Presenze (UL + Pubblici), Personale Iscritto
// - Formazione, Iscrizione Massiva, Docenti
const MACRO_AREAS_CONTENT_CREATOR = [
  {
    id: 'content_creator',
    label: 'Content Creator',
    icon: BookOpen,
    accent: 'from-purple-500/15 to-purple-600/5 border-purple-500/30',
    iconBg: 'bg-purple-600',
    keys: [
      'dashboard',
      // Template & variabili
      'registerTemplates',
      'safetyPrograms',
      'docxTemplatesManager',
      'templateVariables',
      // Contenuti didattici
      'trainingMaterials',
      'trainingTests',
      // Sessioni e registri
      'training',
      'virtualClassrooms',
      'attendanceRegisters',
      'trainingSessions', 'gestioneTemplateFormazione',
      'enrolledPersonnel',
      'teachers'],
  },
  {
    id: 'admin',
    label: 'Notifiche',
    icon: Bell,
    accent: 'from-slate-500/15 to-slate-600/5 border-slate-500/30',
    iconBg: 'bg-slate-600',
    keys: ['notifications'],
  }];

export const Layout = ({ children }) => {
  const { user, logout, refreshUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // v110b — sidebar desktop ridimensionabile (drag handle sul bordo destro).
  // Width persistita in localStorage. Min 220, max 480, default 260.
  const SIDEBAR_MIN = 220;
  const SIDEBAR_MAX = 480;
  const SIDEBAR_DEFAULT = 260;
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem('cen_sidebar_width') || '', 10);
      if (!isNaN(v) && v >= SIDEBAR_MIN && v <= SIDEBAR_MAX) return v;
    } catch (_) {}
    return SIDEBAR_DEFAULT;
  });
  const [isResizing, setIsResizing] = useState(false);
  useEffect(() => {
    if (!isResizing) return;
    const onMove = (e) => {
      const w = Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, e.clientX));
      setSidebarWidth(w);
    };
    const onUp = () => {
      setIsResizing(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isResizing]);
  // Persiste width al cambio (debounced via stato di rilascio)
  useEffect(() => {
    try { localStorage.setItem('cen_sidebar_width', String(sidebarWidth)); } catch (_) {}
  }, [sidebarWidth]);
  const resetSidebarWidth = () => setSidebarWidth(SIDEBAR_DEFAULT);
  // Imposta la CSS variable --cen-sb-w sul root in modo che il main content
  // possa adattare il proprio margine (solo desktop, via media query nel CSS).
  useEffect(() => {
    try {
      document.documentElement.style.setProperty('--cen-sb-w', `${sidebarWidth}px`);
    } catch (_) {}
  }, [sidebarWidth]);

  // Refresh user info on every route change so changes from the admin side
  // (visible_sections, role, is_active...) are visible without a hard reload.
  useEffect(() => {
    if (refreshUser) {
      refreshUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // v1.3.2 — Live server version (bypass JS bundle cache).
  // Mostra nel footer la versione del backend live; se diversa dal bundle
  // attualmente in esecuzione, segnala all'utente di ricaricare (Ctrl+Shift+R).
  const [liveVersion, setLiveVersion] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const fetchLiveVersion = async () => {
      try {
        const base = process.env.REACT_APP_BACKEND_URL || '';
        const res = await fetch(`${base}/api/system/version`, {
          cache: 'no-store',
          headers: { 'Cache-Control': 'no-cache' },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && data?.version) setLiveVersion(data.version);
      } catch (_) {
        // silenziosamente ignora — è solo una info diagnostica
      }
    };
    fetchLiveVersion();
    const id = setInterval(fetchLiveVersion, 60_000); // refresh ogni minuto
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userRole = user?.role || 'collaborator';
  const allowedModules = user?.allowed_modules || [];
  const visibleSections = user?.visible_sections || [];
  const isSuperAdmin = userRole === 'super_admin';
  const isAdmin = userRole === 'super_admin' || userRole === 'admin';
  const isDocente = userRole === 'docente';

  // Super Admin: by default hide everything except licenses/users/registrations.
  // Toggle "Modalità Supporto" persists in sessionStorage.
  const [supportMode, setSupportMode] = useState(
    () => sessionStorage.getItem('cen_support_mode') === '1'
  );
  const toggleSupportMode = () => {
    const next = !supportMode;
    setSupportMode(next);
    sessionStorage.setItem('cen_support_mode', next ? '1' : '0');
  };

  // v1.2.5: moduli extra cross-ruolo dall'utente (impostati dall'admin)
  const extraModules = user?.extra_modules || [];

  // Get filtered items for each group based on role + extra modules
  let mainItems = getFilteredItems('main', userRole, allowedModules, extraModules);
  let documentsItems = getFilteredItems('documents', userRole, allowedModules, extraModules);
  let haccpItems = getFilteredItems('haccp_group', userRole, allowedModules, extraModules);
  let personnelItems = getFilteredItems('personnel', userRole, allowedModules, extraModules);
  let docentiItems = getFilteredItems('docenti', userRole, allowedModules, extraModules);
  let adminItems = getFilteredItems('admin', userRole, allowedModules, extraModules);
  let studioItems = getFilteredItems('studio', userRole, allowedModules, extraModules);

  // HACCP feature gate: l'area HACCP nella sidebar appare SOLO se l'utente
  // ha una licenza HACCP attiva (has_haccp=true) oppure se è super_admin/laboratorio_analisi.
  // v38: per i ruoli "azienda"/"gerente"/"collaborator" senza licenza HACCP,
  // mostriamo la macro-area "Autocontrollo HACCP" con UN SOLO item "Attiva HACCP"
  // che porta alla pagina di richiesta licenza. Tutti gli altri item HACCP
  // (manuale, banchi frigo, temperature, campioni, pulizia, allergeni,
  // check-list ispezione) sono NASCOSTI finché la licenza non è attiva.
  const hasHaccpAccess =
    user?.has_haccp === true ||
    userRole === 'super_admin' ||
    userRole === 'laboratorio_analisi';
  const isAziendaOrUL = ['azienda', 'gerente', 'collaborator'].includes(userRole);
  if (!hasHaccpAccess) {
    if (isAziendaOrUL) {
      // Solo l'item "Attiva HACCP" (upsell) — niente strumenti operativi
      haccpItems = haccpItems.filter((i) => i.key === 'haccpActivate');
    } else {
      // Altri ruoli senza licenza: solo Check-list Ispezione (consultazione report)
      haccpItems = haccpItems.filter((i) => i.key === 'haccpInspection');
    }
  } else {
    // Licenza HACCP attiva: nascondi la voce upsell "Attiva HACCP"
    haccpItems = haccpItems.filter((i) => i.key !== 'haccpActivate');
  }

  // Apply admin-defined `visible_sections` filter on top of role permissions.
  // Super admin and admin are NOT filtered by visible_sections - they always
  // see the full menu set allowed by their role.
  const allowedFromSections = buildAllowedKeysFromVisibleSections(visibleSections, userRole);
  if (allowedFromSections && !isSuperAdmin && userRole !== 'admin') {
    const filterByAllowed = (items) => items.filter((i) => allowedFromSections.has(i.key));
    mainItems = filterByAllowed(mainItems);
    documentsItems = filterByAllowed(documentsItems);
    haccpItems = filterByAllowed(haccpItems);
    personnelItems = filterByAllowed(personnelItems);
    docentiItems = filterByAllowed(docentiItems);
    // v38d: applica filtro anche alle voci Studio Tecnico
    studioItems = filterByAllowed(studioItems);
  }

  // For super_admin: hide operational menus when not in "support mode"
  // L'HACCP viene nascosto al super admin (non serve operativamente — solo
  // le licenze HACCP rimangono visibili in Amministrazione). In Modalità
  // Supporto invece il super admin vede tutto, incluso HACCP dei clienti.
  if (isSuperAdmin && !supportMode) {
    mainItems = mainItems.filter((i) => ['dashboard', 'notifications'].includes(i.key));
    documentsItems = [];
    haccpItems = [];
    personnelItems = [];
    docentiItems = [];
  }

  // Render a group of nav items
  const renderNavGroup = (items, closeOnClick = false) => {
    return items.map((item) => (
      <Link
        key={item.path}
        to={item.path}
        onClick={closeOnClick ? () => setSidebarOpen(false) : undefined}
        className={`sidebar-link ${location.pathname === item.path ? 'active' : ''}`}
        data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
      >
        <item.icon className="w-5 h-5" />
        {item.label}
      </Link>
    ));
  };

  // === Costruzione delle macro-aree (5 sezioni) ===
  // Concateno tutti gli items abilitati per il ruolo, poi li raggruppo per macro-area.
  const allEnabledItems = [
    ...mainItems,
    ...documentsItems,
    ...haccpItems,
    ...personnelItems,
    ...docentiItems,
    ...adminItems,
    ...studioItems];
  const itemByKey = Object.fromEntries(allEnabledItems.map((i) => [i.key, i]));
  // v38: Content Creator (formatore) usa una macro-area dedicata invece delle
  // 5 standard. Tutto ciò che il content creator può fare in un unico gruppo.
  const macroSource = userRole === 'formatore' ? MACRO_AREAS_CONTENT_CREATOR : MACRO_AREAS;
  const macroSections = macroSource.map((m) => ({
    ...m,
    items: m.keys.map((k) => itemByKey[k]).filter(Boolean),
  })).filter((m) => m.items.length > 0);

  // Stato collassato/aperto per macro — comportamento ACCORDION single-open:
  // solo una macro aperta alla volta. Quando cambia la route, si apre la macro
  // che contiene quella route e si chiudono le altre.
  const [openMacroId, setOpenMacroId] = useState(() => {
    try {
      return sessionStorage.getItem('cen_sidebar_open_macro') || null;
    } catch (_) { return null; }
  });
  useEffect(() => {
    const matching = macroSections.find((m) => m.items.some((it) => it.path === location.pathname));
    if (matching && matching.id !== openMacroId) {
      setOpenMacroId(matching.id);
      try { sessionStorage.setItem('cen_sidebar_open_macro', matching.id); } catch (_) {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleMacro = (id) => {
    setOpenMacroId((prev) => {
      const next = prev === id ? null : id;
      try {
        if (next) sessionStorage.setItem('cen_sidebar_open_macro', next);
        else sessionStorage.removeItem('cen_sidebar_open_macro');
      } catch (_) {}
      return next;
    });
  };

  // Render collapsible macro-area
  const renderMacroSection = (m, closeOnClick = false) => {
    const isOpen = openMacroId === m.id;
    const Icon = m.icon;
    const activeInside = m.items.some((it) => it.path === location.pathname);
    return (
      <div key={m.id} className="mb-1.5">
        <button
          type="button"
          onClick={() => toggleMacro(m.id)}
          className={`w-full group flex items-center gap-3 px-2 py-2 rounded-lg border bg-gradient-to-r ${m.accent} hover:shadow-sm transition-all ${activeInside ? 'ring-1 ring-primary/40' : ''}`}
          data-testid={`macro-toggle-${m.id}`}
        >
          <div className={`${m.iconBg} w-8 h-8 rounded-md flex items-center justify-center text-white shadow-sm`}>
            <Icon className="w-4 h-4" />
          </div>
          <span className="flex-1 text-left text-sm font-semibold">{m.label}</span>
          <span className="text-[10px] text-muted-foreground bg-white/70 dark:bg-black/30 px-1.5 py-0.5 rounded">
            {m.items.length}
          </span>
          <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${isOpen ? 'rotate-90' : ''}`} />
        </button>
        <AnimatePresence initial={false}>
          {isOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.18 }}
              className="overflow-hidden"
            >
              <div className="pl-3 pr-1 py-1 space-y-0.5 border-l border-border/40 ml-4 mt-1">
                {renderNavGroup(m.items, closeOnClick)}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  // Render separator
  const renderSeparator = () => (
    <div className="my-3 border-t border-border/50"></div>
  );

  // Render section header
  const renderSectionHeader = (title) => (
    <div className="pt-4 pb-2">
      <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
        {title}
      </span>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 glass-header border-b border-border px-4 py-3">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 hover:bg-secondary rounded-sm transition-colors"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <CENLogo />
            <span className="font-heading font-bold text-lg">CEN</span>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-2 hover:bg-secondary rounded-full transition-colors" data-testid="user-menu-mobile">
                <User className="w-5 h-5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem disabled className="text-sm text-muted-foreground">
                {user?.email}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/profile')}>
                <User className="w-4 h-4 mr-2" />
                Profilo
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/profilo/firma-timbro')} data-testid="menu-firma-timbro">
                <FileSignature className="w-4 h-4 mr-2" />
                Firma & Timbro
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                <LogOut className="w-4 h-4 mr-2" />
                Esci
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Mobile sidebar overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-40 lg:hidden"
              onClick={() => setSidebarOpen(false)}
            />
            <motion.aside
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed left-0 top-0 bottom-0 w-[280px] bg-background border-r border-border z-50 lg:hidden overflow-y-auto"
            >
              <div className="p-4 flex items-center justify-between border-b border-border">
                <div className="flex items-center gap-2">
                  <CENLogo />
                  <div>
                    <h1 className="font-heading font-bold text-lg">CEN Engineering</h1>
                    <p className="text-xs text-muted-foreground">Safety Portal</p>
                  </div>
                </div>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="p-2 hover:bg-secondary rounded-sm transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              {/* v1.3.9 — Selettore contesto Società + UL (opzionale) */}
              <SidebarContextSelector />
              <nav className="p-4 space-y-1">
                {/* === MACRO-AREE COLLASSABILI (mobile) === */}
                {macroSections.map((m) => renderMacroSection(m, true))}

                {/* Super Admin: toggle "Modalità Supporto" (mobile) */}
                {isSuperAdmin && (
                  <>
                    <div className="pt-3 mt-3 border-t border-border/50" />
                    <button
                      type="button"
                      onClick={toggleSupportMode}
                      className={`sidebar-link w-full justify-between ${supportMode ? 'bg-amber-500/10 text-amber-700' : ''}`}
                      data-testid="toggle-support-mode-btn-mobile"
                    >
                      <span className="flex items-center gap-2">
                        <Settings className="w-5 h-5" />
                        Modalità Supporto
                      </span>
                      <span className={`text-xs font-bold px-2 py-0.5 rounded ${supportMode ? 'bg-amber-600 text-white' : 'bg-muted text-muted-foreground'}`}>
                        {supportMode ? 'ON' : 'OFF'}
                      </span>
                    </button>
                  </>
                )}
              </nav>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Desktop sidebar */}
      <aside
        className="hidden lg:flex flex-col fixed left-0 top-0 bottom-0 bg-background border-r border-border group/sidebar"
        style={{ width: `${sidebarWidth}px` }}
        data-testid="desktop-sidebar"
      >
        <div className="p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <CENLogo />
            <div className="min-w-0">
              <h1 className="font-heading font-black text-xl tracking-tight truncate">CEN Engineering</h1>
              <p className="text-xs text-muted-foreground mt-0.5 uppercase tracking-widest truncate">Safety Portal</p>
            </div>
          </div>
        </div>

        {/* v1.3.9 — Selettore contesto Società + UL (opzionale) */}
        <SidebarContextSelector />

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {/* === MACRO-AREE COLLASSABILI (desktop) === */}
          {macroSections.map((m) => renderMacroSection(m, false))}

          {/* Super Admin: toggle "Modalità Supporto" */}
          {isSuperAdmin && (
            <>
              <div className="pt-3 mt-3 border-t border-border/50" />
              <button
                type="button"
                onClick={toggleSupportMode}
                className={`sidebar-link w-full justify-between ${supportMode ? 'bg-amber-500/10 text-amber-700' : ''}`}
                data-testid="toggle-support-mode-btn"
                title="Mostra/nascondi i dati operativi dei clienti"
              >
                <span className="flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Modalità Supporto
                </span>
                <span className={`text-xs font-bold px-2 py-0.5 rounded ${supportMode ? 'bg-amber-600 text-white' : 'bg-muted text-muted-foreground'}`}>
                  {supportMode ? 'ON' : 'OFF'}
                </span>
              </button>
              <p className="text-[10px] text-muted-foreground px-3 pb-2">
                {supportMode ? 'Vedi tutti i dati dei clienti (per supporto)' : 'Dati clienti nascosti — solo licenze e utenti'}
              </p>
            </>
          )}
        </nav>

        <div className="p-4 border-t border-border">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button 
                className="w-full flex items-center gap-3 p-3 rounded-sm hover:bg-secondary transition-colors text-left"
                data-testid="user-menu-desktop"
              >
                <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-semibold">
                  {user?.full_name?.[0]?.toUpperCase() || 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{user?.full_name}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                </div>
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[230px]">
              <DropdownMenuItem disabled className="text-xs text-muted-foreground">
                Ruolo: {roleDisplayNames[userRole] || 'Azienda'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/profile')}>
                <User className="w-4 h-4 mr-2" />
                Profilo
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/profilo/firma-timbro')}>
                <FileSignature className="w-4 h-4 mr-2" />
                Firma & Timbro
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                <LogOut className="w-4 h-4 mr-2" />
                Esci
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          {/* Footer */}
          <div className="mt-4 pt-4 border-t border-border text-center">
            <p className="text-xs text-muted-foreground">
              © 2026 CEN Engineering
            </p>
            <p className="text-[10px] text-muted-foreground/70 mt-1" data-testid="app-version">
              Bundle {APP_VERSION}
              {liveVersion && liveVersion !== APP_VERSION && (
                <span className="text-amber-600 font-semibold" title="Il backend ha una versione più recente. Premi Ctrl+Shift+R per ricaricare."> · server {liveVersion} ⚠</span>
              )}
              {liveVersion && liveVersion === APP_VERSION && (
                <span className="text-emerald-600"> · server {liveVersion} ✓</span>
              )}
            </p>
          </div>
        </div>

        {/* v110b — Drag handle per ridimensionare la sidebar */}
        <div
          onMouseDown={() => setIsResizing(true)}
          onDoubleClick={resetSidebarWidth}
          title="Trascina per ridimensionare • Doppio click per resettare"
          data-testid="sidebar-resize-handle"
          className={`absolute top-0 right-0 h-full w-1.5 cursor-col-resize transition-colors z-10 ${
            isResizing
              ? 'bg-indigo-500/60'
              : 'bg-transparent hover:bg-indigo-500/30 group-hover/sidebar:bg-border'
          }`}
        >
          <div className="absolute top-1/2 -translate-y-1/2 right-0 w-1 h-12 rounded-l bg-border/70 group-hover/sidebar:bg-indigo-500/70" />
        </div>
      </aside>

      {/* Main content */}
      <main
        className="min-h-screen pt-16 lg:pt-0 transition-[margin] duration-100 cen-main-with-sidebar"
        data-testid="main-content"
      >
        {/* v1.3.9 — CurrentUlBanner rimosso: i selettori Società + UL sono ora nella sidebar */}
        <DocenteKeysBanner />
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="p-6 lg:p-8"
        >
          {children}
        </motion.div>
      </main>
      <AiAssistant />
    </div>
  );
};

// Banner showing the currently selected Unità Locale (scoping context).
const CurrentUlBanner = () => {
  const { currentPdvId, setCurrentPdvId, clearCurrentPdv } = useCurrentPdv();
  const [pdvList, setPdvList] = useState([]);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');

  // Load all PDVs available to the user (used for both name lookup + dropdown)
  useEffect(() => {
    let aborted = false;
    pdvApi
      .getAll()
      .then((r) => { if (!aborted) setPdvList(r.data || []); })
      .catch(() => { if (!aborted) setPdvList([]); });
    return () => { aborted = true; };
  }, [currentPdvId]);

  const currentPdv = pdvList.find((p) => p.id === currentPdvId);
  const pdvName = currentPdv?.name || '';

  // Close dropdown on escape / outside click
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onClick = (e) => {
      if (!e.target.closest('[data-ul-dropdown-root]')) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onClick);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  // Don't render the banner if the user has no PDV access at all
  if (pdvList.length === 0) return null;
  // If no UL is currently selected, still show a compact selector (so users can pick one globally)
  const filtered = filter
    ? pdvList.filter((p) =>
        (p.name || '').toLowerCase().includes(filter.toLowerCase()) ||
        (p.company_name || '').toLowerCase().includes(filter.toLowerCase()) ||
        (p.city || '').toLowerCase().includes(filter.toLowerCase()),
      )
    : pdvList;

  return (
    <div
      className="relative bg-primary/10 border-b border-primary/20 px-4 lg:px-8 py-2 flex items-center justify-between text-sm gap-2"
      data-testid="current-ul-banner"
      data-ul-dropdown-root
    >
      <div className="flex items-center gap-2 text-primary min-w-0">
        <Store className="w-4 h-4 shrink-0" />
        <span className="hidden sm:inline">Stai operando su:</span>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="relative inline-flex items-center gap-1 font-semibold underline-offset-2 hover:underline truncate max-w-[60vw] sm:max-w-md"
          data-testid="ul-dropdown-trigger"
        >
          {pdvName || <span className="italic font-normal">Seleziona Unità Locale...</span>}
          <ChevronDown className="w-3.5 h-3.5 shrink-0" />
        </button>

        {open && (
          <div
            className="absolute left-0 right-0 top-full mt-1 mx-4 lg:mx-8 z-50 bg-white border border-primary/20 shadow-lg rounded-md max-h-[60vh] overflow-hidden flex flex-col"
            data-testid="ul-dropdown-list"
          >
            <div className="p-2 border-b">
              <input
                type="text"
                autoFocus
                placeholder="Cerca per nome, azienda o città..."
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="w-full text-sm px-2 py-1.5 border rounded outline-none focus:ring-1 focus:ring-primary"
                data-testid="ul-dropdown-search"
              />
            </div>
            <div className="overflow-auto">
              {filtered.length === 0 && (
                <div className="px-4 py-3 text-xs text-muted-foreground italic">Nessuna UL trovata.</div>
              )}
              {filtered.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => {
                    setCurrentPdvId(p.id);
                    setOpen(false);
                    setFilter('');
                  }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-primary/10 transition-colors flex flex-col gap-0.5 ${
                    p.id === currentPdvId ? 'bg-primary/15 font-semibold' : ''
                  }`}
                  data-testid={`ul-dropdown-item-${p.id}`}
                >
                  <span className="truncate">{p.name}</span>
                  {(p.company_name || p.city) && (
                    <span className="text-[11px] text-muted-foreground truncate">
                      {[p.company_name, p.city].filter(Boolean).join(' — ')}
                    </span>
                  )}
                </button>
              ))}
            </div>
            {currentPdvId && (
              <div className="border-t p-2">
                <button
                  type="button"
                  onClick={() => { clearCurrentPdv(); setOpen(false); }}
                  className="w-full text-center text-xs text-primary/80 hover:text-primary py-1 underline"
                  data-testid="ul-dropdown-clear"
                >
                  Rimuovi filtro UL · Mostra tutte
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      {currentPdvId && (
        <button
          type="button"
          onClick={clearCurrentPdv}
          className="text-xs underline text-primary/80 hover:text-primary shrink-0"
          data-testid="clear-current-ul-btn"
        >
          Mostra tutte
        </button>
      )}
    </div>
  );
};

// v1.1.1: Banner saldo chiavi RIATTIVATO per docente_online
// Mostra avviso quando le chiavi sono basse / esaurite, con CTA verso "Le mie Chiavi".
const DocenteKeysBanner = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (user?.role !== 'docente_online') return;
    let aborted = false;
    docenteKeysApi
      .myKeys()
      .then((r) => { if (!aborted) setData(r.data); })
      .catch(() => { if (!aborted) setData(null); });
    return () => { aborted = true; };
  }, [user?.role, location.pathname]);

  if (user?.role !== 'docente_online' || !data) return null;
  const available = data.available || 0;
  const hasSub = !!data.subscription;
  if (hasSub) return null;
  if (available > 3) return null;
  if (location.pathname === '/docente-keys') return null;

  const isEmpty = available === 0;
  const styles = isEmpty
    ? 'bg-red-50 border-b border-red-200 text-red-800'
    : 'bg-amber-50 border-b border-amber-200 text-amber-900';
  const label = isEmpty
    ? 'Hai esaurito le chiavi disponibili. Acquistane di nuove per creare aule virtuali.'
    : `Hai solo ${available} ${available === 1 ? 'chiave residua' : 'chiavi residue'}. Acquistane di nuove prima che si esauriscano.`;
  return (
    <div
      className={`${styles} px-4 lg:px-8 py-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm`}
      data-testid="docente-keys-banner"
    >
      <div className="flex items-center gap-2">
        <Key className="w-4 h-4" />
        <span data-testid="docente-keys-banner-message">{label}</span>
      </div>
      <button
        type="button"
        onClick={() => navigate('/docente-keys')}
        className={`inline-flex items-center gap-1 self-start sm:self-auto px-3 py-1 rounded font-semibold text-xs ${
          isEmpty ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-amber-600 text-white hover:bg-amber-700'
        }`}
        data-testid="docente-keys-banner-cta"
      >
        Acquista chiavi
      </button>
    </div>
  );
};

export default Layout;
